<script setup lang="ts">
/**
 * **LE PETIT PERSONNAGE DES ÉTATS VIDES** — dans l'esprit des illustrations du
 * site public (`mankomail-cloud/…/components/public/art/person.blade.php`) :
 * formes pleines, sans contour ni dégradé, ni emoji ni 3D ; une peau, des
 * cheveux marine (`hair`, le même dans les deux modes), un haut cobalt (`brand`), et UN objet qui dit la situation.
 *
 *   empty  — un bac de rangement vide à côté de lui : « rien ici, pour
 *            l'instant » ;
 *   search — une loupe : « introuvable » (404, recherche sans résultat) ;
 *   done   — une coche verte et le sourire : « rien ne vous attend ».
 *
 * Toutes les couleurs sont des tokens (`fill-brand`, `fill-skin`,
 * `fill-heading`…) : le personnage passe en sombre avec la palette, et une
 * marque blanche ne le recolore pas (le décor reste celui du produit — seuls
 * l'accent et le bouton principal sont de la marque).
 *
 * Décoratif (`aria-hidden`) : le titre de l'état vide porte le sens. Le
 * personnage respire (`animate-float`, 3 px sur 6 s), coupé sous
 * `prefers-reduced-motion`.
 */
withDefaults(defineProps<{ scene?: 'empty' | 'search' | 'done' }>(), { scene: 'empty' });
</script>

<template>
  <svg
    viewBox="0 0 160 112"
    class="h-auto w-40"
    aria-hidden="true"
    focusable="false"
    :data-scene="scene"
    data-testid="empty-illustration"
  >
    <!-- Le sol : une ombre douce, le seul aplat qui ne bouge pas. -->
    <ellipse cx="80" cy="106" rx="62" ry="5" class="fill-brand-soft" />
    <!-- Le halo derrière le personnage, et un soleil discret. -->
    <circle cx="62" cy="58" r="40" class="fill-brand-soft" />
    <circle cx="128" cy="22" r="6" class="fill-sun" />

    <g class="animate-float">
      <g transform="translate(26 42)">
        <path class="fill-brand" d="M10 64c0-12 9-19 22-19s22 7 22 19Z" />
        <path class="fill-skin" d="M27 40h10v8a5 5 0 0 1-10 0Z" />
        <circle class="fill-skin" cx="32" cy="29" r="12" />
        <path
          class="fill-hair"
          d="M20 27c0-8 5-12 12-12s12 4 12 11c-3-2-6-4-11-4-5 0-9 2-13 5Z"
        />
        <path
          v-if="scene === 'done'"
          class="fill-none stroke-hair"
          stroke-width="1.6"
          stroke-linecap="round"
          d="M26 28.5q2-2 4 0M34 28.5q2-2 4 0M28 34q4 4 8 0"
        />
        <path
          v-else
          class="fill-none stroke-hair"
          stroke-width="1.6"
          stroke-linecap="round"
          d="M27 28.5v1.5M37 28.5v1.5M29 35q3 2 6 0"
        />
        <circle class="fill-skin-deep" cx="25" cy="33" r="1.8" />
        <circle class="fill-skin-deep" cx="39" cy="33" r="1.8" />
      </g>
    </g>

    <!-- L'objet de la scène, à droite du personnage. -->
    <g v-if="scene === 'empty'">
      <path class="fill-surface-raised stroke-heading" stroke-width="2" stroke-linejoin="round" d="M98 78h44l-5 26h-34Z" />
      <path class="fill-none stroke-heading" stroke-width="2" stroke-linecap="round" d="M106 88h28" />
      <path class="fill-brand" d="M96 74h48v6H96Z" />
    </g>
    <g v-else-if="scene === 'search'">
      <circle class="fill-surface-raised stroke-heading" stroke-width="3" cx="118" cy="70" r="15" />
      <path class="fill-none stroke-brand" stroke-width="5" stroke-linecap="round" d="M129 81l12 12" />
      <path class="fill-none stroke-heading" stroke-width="2" stroke-linecap="round" d="M112 64q4-4 9-2" />
    </g>
    <g v-else>
      <circle class="fill-success" cx="122" cy="76" r="16" />
      <path class="fill-none stroke-surface-raised" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" d="m114 76 6 6 10-12" />
    </g>
  </svg>
</template>
